<template>
  <div
    class="product-gallery"
    :class="`product-gallery--${responsiveLayout}`"
  >
    <div class="product-gallery__main-container">
      <transition name="fade">
        <button
          v-if="showZoomButton"
          class="product-gallery__zoom"
          type="button"
          @click.prevent="handleZoomClick"
        >
          <icon-zoom-in />

          <span
            class="text-other-label-s text-other-label-l-desktop"
            v-text="$string('product.gallery.zoom')"
          />
        </button>
      </transition>

      <product-gallery-carousel
        class="product-gallery__main-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.main"
        :layout="responsiveLayout"
        :loading="loading"
        namespace="gallery"
        :product="product"
        :sizes="sizes"
        type="main"
        :zoom="zoom"
        @update-current-index="handleUpdateCurrentIndex"
      />
    </div>

    <div
      v-if="showThumbnails"
      class="product-gallery__thumbnails-container"
    >
      <product-gallery-carousel
        class="product-gallery__thumbnails-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.thumbnails"
        :layout="responsiveLayout"
        :loading="loading"
        min-max="100-300"
        namespace="gallery"
        :product="product"
        sizes="102px"
        type="thumbnails"
        @update-current-index="handleUpdateCurrentIndex"
      />
    </div>

    <!-- Product gallery modal -->
    <overlay
      v-if="zoom"
      key="productGalleryModal"
      block-class="product-gallery-modal-overlay"
      direction="up"
      namespace="productGalleryModal"
      :show-overlay="false"
      :template="false"
      type="modal"
    >
      <template #body="overlay">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          namespace="modal"
          :overlay="overlay"
          :product="product"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Wrapper for product gallery carousel.
 *
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {String} layout - Product gallery layout, either `horizontal`,
 * `vertical`, `tabs`, or `grid`, supports a combination in the format
 * [mobile layout]-[desktop layout].
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} product - Local product object.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 *
 * @emits updateCurrentIndex - Emit update current carousel index event.
 * @emits updateCurrentNamespace - Emit update current carousel namespace event.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import ProductGalleryCarousel from '~async/product-gallery-carousel/product-gallery-carousel'

import IconZoomIn from '~icons/directional-navigation/zoom-in.svg'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ProductGallery',

  components: {
    IconZoomIn,
    Overlay,
    ProductGalleryCarousel,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      validator: (value) => {
        return ['gallery', 'modal'].includes(value)
      },
    },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (value) => {
        return value.split('-').every((part) => ['grid', 'horizontal', 'tabs', 'vertical'].includes(part))
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, null, Object],
      default: () => ({}),
    },
    zoom: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'update-current-index': (payload) => typeof payload === 'number',
    'update-current-namespace': (payload) => ['gallery', 'modal'].includes(payload),
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute carousel configs.
     * - The aim here is to convert the responsive layout into the breakpoints
     *   object supported by Splide.
     * @returns {Object}
     */
    config() {
      const mainConfigs = {
        grid: {
          arrows: false,
          destroy: true,
          pagination: false,
        },
        horizontal: {
          arrows: true,
          destroy: false,
          pagination: false,
        },
        tabs: {
          arrows: true,
          destroy: false,
          pagination: true,
        },
        vertical: {
          arrows: true,
          destroy: false,
          pagination: false,
        },
      }

      const thumbnailsConfigs = {
        shared: {
          arrows: true,
          drag: false,
          focus: 1,
          pagination: false,
          perMove: 5,
          perPage: 5,
          type: 'slide',
        },
        horizontal: {
          gap: 'var(--spacing-m)',
        },
        vertical: {
          direction: 'ttb',
          fixedHeight: '70px',
          gap: 'var(--spacing-xs)',
          height: 'calc((70px + var(--spacing-xs)) * 5)',
          trimSpace: true,
        },
        verticalBreakpoints: {
          [breakpoints.m]: {
            height: 'calc((70px + var(--spacing-xs)) * 5)',
          },
          [breakpoints.l]: {
            height: 'calc((70px + var(--spacing-xs)) * 3)',
          },
          [breakpoints.xl]: {
            height: 'calc((70px + var(--spacing-xs)) * 5)',
          },
        },
      }

      /**
       * Set configs.
       * - No difference between mobile and desktop layouts.
       */
      if (!this.layout.includes('-')) {
        const config = {
          main: mainConfigs[this.layout],
          thumbnails: {
            ...thumbnailsConfigs.shared,
            ...thumbnailsConfigs[this.layout],
          },
        }

        if (this.layout === 'vertical') {
          config.thumbnails.breakpoints = thumbnailsConfigs.verticalBreakpoints
        }

        return config
      }

      /**
       * Set mobile and desktop layouts independently.
       */
      const mobileLayout = this.layout.split('-')[0]
      const desktopLayout = this.layout.split('-')[1]

      const config = {
        main: {
          ...mainConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...mainConfigs[desktopLayout],
            },
          },
        },
        thumbnails: {
          ...thumbnailsConfigs.shared,
          ...thumbnailsConfigs[mobileLayout],
          breakpoints: {
            [breakpoints.l]: {
              ...thumbnailsConfigs[desktopLayout],
            },
          },
        },
      }

      /**
       * Add vertical layout breakpoints.
       */
      if (mobileLayout === 'vertical') {
        config.thumbnails.breakpoints = thumbnailsConfigs.verticalBreakpoints[breakpoints.m]
      }

      if (desktopLayout === 'vertical') {
        config.thumbnails.breakpoints = {
          [breakpoints.l]: {
            ...config.thumbnails.breakpoints[breakpoints.l],
            ...thumbnailsConfigs.verticalBreakpoints[breakpoints.l],
          },
          [breakpoints.xl]: thumbnailsConfigs.verticalBreakpoints[breakpoints.xl],
        }
      }

      /**
       * Return config.
       */
      return config
    },

    /**
     * Compute responsive layout based on mobile.
     * @returns {String}
     */
    responsiveLayout() {
      if (!this.layout.includes('-')) {
        return this.layout
      }

      return this.isMobile
        ? this.layout.split('-')[0]
        : this.layout.split('-')[1]
    },

    /**
     * Compute whether to show thumbnails carousel.
     * @returns {Boolean}
     */
    showThumbnails() {
      return (
        this.product?.media?.length > 1 &&
        (this.responsiveLayout === 'horizontal' || this.responsiveLayout === 'vertical')
      )
    },

    /**
     * Compute whether to show zoom button.
     * - Button is hidden when video media is active.
     * @returns {Boolean}
     */
    showZoomButton() {
      const currentMedia = this.product?.media?.length
        ? this.product?.media[this.currentIndex]
        : false

      return (
        this.zoom &&
        !this.loading &&
        currentMedia &&
        (currentMedia?.mediaType === 'image' || !currentMedia?.mediaType)
      )
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}px) 50vw, 100vw`
    },
  },

  watch: {

    /**
     * Watch variant to show matching image if it exists.
     */
    variant() {
      if (!this.variant) {
        return
      }

      /**
       * Match media (either media or images array) with associated variant.
       */
      const matchedImageIndex = this.product?.media?.findIndex((media) => {
        return media.variant === this.variant.id
      })

      if (matchedImageIndex < 0) {
        return
      }

      this.$emit('update-current-index', matchedImageIndex)
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Emit update current carousel index event.
     * @param {Number} newIndex - New media index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.$emit('update-current-index', newIndex)
    },

    /**
     * Emit update current carousel namespace event.
     * @param {Number} newNamespace - New active namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.$emit('update-current-namespace', newNamespace)
    },

    /**
     * Handle zoom click.
     */
    handleZoomClick() {
      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-gallery';
@import './product-gallery-grid';
@import './product-gallery-tabs';
// Disable unused layout stylesheets
// @import './product-gallery-horizontal';
// @import './product-gallery-vertical';
</style>
